import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import { RESOURCES, RESOURCES_CATEGORIES, RESOURCES_BY_CATEGORY } from "@/lib/resources";

export default function ResourcesPage() {
  const { user } = useAuth();
  const featured = RESOURCES.slice(0, 3);

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-[#F5F2EC] bg-grain border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-600 font-bold mb-3">Ressources hôtes</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95]">
              L'école de l'hospitalité <em className="italic font-light">bretonne.</em>
            </h1>
            <p className="mt-6 text-lg text-neutral-700 max-w-xl leading-relaxed">
              Tout ce qu'il faut savoir pour devenir un hôte serein, rentable, et apprécié. Pensé pour la Bretagne, écrit par notre équipe à Beaussais-Sur-Mer.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={user?.role === "owner" ? "/owner" : "/register?role=owner"} className="bg-[#0A0A0A] text-white px-7 py-3.5 font-semibold hover:bg-[#1B2A49] transition-colors flex items-center gap-2" data-testid="res-cta-host">
                {user?.role === "owner" ? "Mon espace hôte" : "Démarrer maintenant"} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#categories" className="border-2 border-[#0A0A0A] px-7 py-3.5 font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors">Parcourir les guides</a>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-black/10 px-5 py-4 shadow-xl max-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Guides publiés</div>
              <div className="font-display text-4xl font-bold tracking-tight mt-1">{RESOURCES.length}</div>
              <div className="text-xs text-neutral-600 mt-1">Spécifiques Bretagne</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Par thématique</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">Choisissez votre <em className="italic font-light">terrain de jeu.</em></h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {RESOURCES_CATEGORIES.map((cat) => {
            const count = RESOURCES_BY_CATEGORY[cat.slug]?.length || 0;
            return (
              <Link to={`#cat-${cat.slug}`} key={cat.slug} className="group border border-black/10 p-7 hover:border-[#0A0A0A] transition-colors bg-white" data-testid={`res-cat-${cat.slug}`}>
                <div className="text-4xl mb-4 group-hover:scale-110 origin-left transition-transform">{cat.emoji}</div>
                <div className="font-display text-2xl font-bold leading-tight">{cat.label}</div>
                <div className="text-sm text-neutral-600 mt-2 leading-relaxed">{cat.desc}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mt-5">{count} guide{count > 1 ? "s" : ""}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-[#0A0A0A] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-bold mb-3">À lire en priorité</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">Les essentiels.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {featured.map((r) => (
              <Link to={`/ressources/${r.slug}`} key={r.slug} className="group block bg-white text-[#0A0A0A] overflow-hidden hover:translate-y-[-3px] transition-transform">
                <div className="aspect-[3/2] overflow-hidden">
                  <img src={r.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{RESOURCES_CATEGORIES.find((c) => c.slug === r.category)?.label}</div>
                  <div className="font-display text-xl font-bold mt-2 leading-tight">{r.title}</div>
                  <div className="flex items-center gap-2 mt-4 text-xs text-neutral-500">
                    <Clock className="w-3 h-3" /> {r.readTime}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ALL BY CATEGORY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-20">
        {RESOURCES_CATEGORIES.map((cat) => {
          const items = RESOURCES_BY_CATEGORY[cat.slug] || [];
          if (!items.length) return null;
          return (
            <div key={cat.slug} id={`cat-${cat.slug}`}>
              <div className="flex items-end justify-between mb-8 border-b border-black/10 pb-4">
                <div>
                  <div className="text-3xl mb-1">{cat.emoji}</div>
                  <h2 className="font-display text-3xl font-bold tracking-tight">{cat.label}</h2>
                  <p className="text-sm text-neutral-600 mt-1">{cat.desc}</p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{items.length} guide{items.length > 1 ? "s" : ""}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((r) => (
                  <Link to={`/ressources/${r.slug}`} key={r.slug} className="group flex gap-4 border border-black/10 p-5 hover:border-[#0A0A0A] transition-colors bg-white" data-testid={`res-article-${r.slug}`}>
                    <div className="w-32 aspect-square overflow-hidden shrink-0">
                      <img src={r.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-bold leading-tight group-hover:text-[#1B2A49]">{r.title}</div>
                      <div className="text-xs text-neutral-600 mt-1.5 line-clamp-2 leading-relaxed">{r.excerpt}</div>
                      <div className="flex items-center gap-2 mt-3 text-[10px] text-neutral-500 uppercase tracking-[0.18em]">
                        <Clock className="w-3 h-3" /> {r.readTime}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA */}
      <section className="bg-[#F5F2EC] py-20 border-t border-black/10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <BookOpen className="w-10 h-10 mx-auto text-[#7A1535]" />
          <h2 className="font-display text-4xl font-bold tracking-tight mt-5">Prêt à vous lancer ?</h2>
          <p className="text-neutral-600 mt-3 max-w-xl mx-auto">Notre équipe est joignable au <a href="tel:+33972158090" className="font-bold underline">09 72 15 80 90</a> de 8h à 23h tous les jours pour vous accompagner pas à pas.</p>
          <Link to={user?.role === "owner" ? "/owner" : "/register?role=owner"} className="inline-flex items-center gap-2 mt-7 bg-[#0A0A0A] text-white px-8 py-3.5 font-semibold hover:bg-[#1B2A49] transition-colors">
            Créer mon annonce <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
